import { createContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLocation } from 'wouter';
import type { HelpContent } from '@/content/help/types';
import { getHelpContentForRoute, getHelpContentById } from '@/content/help';

export interface HelpContextType {
  isOpen: boolean;
  content: HelpContent | null;
  searchQuery: string;
  openHelp: (contentId?: string) => void;
  closeHelp: () => void;
  toggleHelp: () => void;
  setSearchQuery: (query: string) => void;
}

export const HelpContext = createContext<HelpContextType>({
  isOpen: false,
  content: null,
  searchQuery: '',
  openHelp: () => {},
  closeHelp: () => {},
  toggleHelp: () => {},
  setSearchQuery: () => {},
});

interface HelpProviderProps {
  children: ReactNode;
}

export function HelpProvider({ children }: HelpProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<HelpContent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [location] = useLocation();

  // Update content when location changes (if panel is open)
  useEffect(() => {
    if (isOpen && !content) {
      const routeContent = getHelpContentForRoute(location);
      setContent(routeContent);
    }
  }, [location, isOpen, content]);

  const openHelp = useCallback(
    (contentId?: string) => {
      if (contentId) {
        // Open with specific content
        const specificContent = getHelpContentById(contentId);
        setContent(specificContent);
      } else {
        // Open with content for current route
        const routeContent = getHelpContentForRoute(location);
        setContent(routeContent);
      }
      setIsOpen(true);
      setSearchQuery('');
    },
    [location]
  );

  const closeHelp = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  const toggleHelp = useCallback(() => {
    if (isOpen) {
      closeHelp();
    } else {
      openHelp();
    }
  }, [isOpen, openHelp, closeHelp]);

  // Keyboard shortcut: Press "?" to toggle help
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger in input fields, textareas, or content-editable elements
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (event.key === '?' && !isInputField) {
        event.preventDefault();
        toggleHelp();
      }

      // ESC to close
      if (event.key === 'Escape' && isOpen) {
        closeHelp();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleHelp, isOpen, closeHelp]);

  return (
    <HelpContext.Provider
      value={{
        isOpen,
        content,
        searchQuery,
        openHelp,
        closeHelp,
        toggleHelp,
        setSearchQuery,
      }}
    >
      {children}
    </HelpContext.Provider>
  );
}
